import apiClient from '@/api/ApiClient';
import i18n from '@/locales/i18n';
import currentRepresentationKey from '@/utility/representationKey';

const state = {
  representatives: [],
  currentRepresentation: {},
  representationSearchWord: '',
};

const matchesOrgnr = (value) => /^\d{9}$/.exec(value) !== null;

const filterFunctions = {
  search(item) {
    const searchTerms = (state.representationSearchWord.toString().trim() || '').toLowerCase();
    return (
      (item.navn && item.navn.toLowerCase().includes(searchTerms)) ||
      (item.identifikator && item.identifikator.toLowerCase().includes(searchTerms))
    );
  },
};

const filterRepresentatives = (state) => {
  let filterRepresentation = state.representatives.filter((item) => item.erPrivatPerson !== true);
  if (state.representationSearchWord.length > 0) {
    filterRepresentation = filterRepresentation.filter(filterFunctions.search);
  }
  return filterRepresentation;
};

export const getters = {
  getCurrentRepresentation: (state) => state.currentRepresentation,
  getOrgsToRepresent: (state) => state.representatives.filter((item) => item.erPrivatPerson !== true),
  getOrgsToRepresentFiltered: (state) => filterRepresentatives(state),
  isRepresentingAnOrganization: (state) => {
    if (Object.keys(state.currentRepresentation).length === 0) return false;
    return matchesOrgnr(state.currentRepresentation.identifikator);
  },
  hasCurrentRepresentation: (state) => Object.keys(state.currentRepresentation).length,
};

const actions = {
  async getRepresentations({ commit, dispatch }) {
    try {
      const representatives = await apiClient.getRepresentations();

      commit('SET_REPRESENTATIONS', representatives);

      const storedUser = JSON.parse(localStorage.getItem(currentRepresentationKey));
      const representationTimeout = 24 * 60 * 60 * 1000; // 24 hours

      if (representatives.length === 1) {
        commit('SET_CURRENT_REPRESENTATION', representatives[0]);
      } else if (
        storedUser &&
        storedUser.brukerId &&
        new Date().getTime() - storedUser.lastChanged < representationTimeout
      ) {
        const representasjon = representatives.find((user) => user.identifikator === storedUser.brukerId);
        if (representasjon && representasjon.identifikator) {
          commit('SET_CURRENT_REPRESENTATION', representasjon);
        }
      }

      return Promise.resolve();
    } catch (e) {
      dispatch(
        'addGlobalMessage',
        { message: i18n.t('shared.error.representation'), messageType: 'error' },
        { root: true }
      );
      commit('SET_REPRESENTATIONS', []);
      return Promise.reject();
    }
  },

  setRepresentationsSearchWord({ commit }, searchWord) {
    commit('SET_REPRESENTATIONS_SEARCH_WORD', searchWord);
  },

  setCurrentRepresentation({ commit }, representation) {
    const representationStorageValue = JSON.stringify({
      brukerId: representation.identifikator.toString(),
      lastChanged: new Date().getTime(),
    });
    localStorage.setItem(currentRepresentationKey, representationStorageValue);
    sessionStorage.setItem(currentRepresentationKey, representationStorageValue);
    sessionStorage.setItem('app.minside/referrer', window.location); // Needed to preserve representation in minside
    sessionStorage.setItem('returnTo', window.location); // Needed to preserve representation in minside
    commit('SET_CURRENT_REPRESENTATION', representation);
  },

  removeStorageItems() {
    return new Promise((resolve) => {
      localStorage.removeItem(currentRepresentationKey);
      sessionStorage.removeItem('app.minside/referrer');
      sessionStorage.removeItem(currentRepresentationKey);
      sessionStorage.removeItem('returnTo');
      resolve();
    });
  },
};

const mutations = {
  SET_REPRESENTATIONS(state, representatives) {
    state.representatives = representatives.map((rep) => ({
      ...rep,
      formatted: matchesOrgnr(rep.identifikator) ? `${rep.identifikator} - ${rep.navn}` : rep.navn,
    }));
  },
  SET_REPRESENTATIONS_SEARCH_WORD(state, searchWord) {
    state.representationSearchWord = searchWord;
  },
  SET_CURRENT_REPRESENTATION(state, representation) {
    state.currentRepresentation = representation;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
